import logo from '@/assets/hiredcoach-logo.svg';
import UserAvatar from '@/components/avatar/UserAvatar';
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { JobApiResponse, PitchAnalysisResponse, useApiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, CheckCircle, Clock, ExternalLink, Lightbulb, MessageCircle, Plus, Target, TrendingUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

// Job Name Display Component for PitchAnalysis
const JobNameDisplay: React.FC<{ jobId: string; demoMode?: boolean }> = ({ jobId, demoMode = false }) => {
    const { api } = useApiClient();
    const navigate = useNavigate();
    const [job, setJob] = useState<JobApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const demoModeRef = useRef(demoMode);

    // Update ref when demoMode changes
    useEffect(() => {
        demoModeRef.current = demoMode;
    }, [demoMode]);

    // Initialize demo job immediately if in demo mode
    useEffect(() => {
        if (demoMode) {
            setJob({
                id: jobId,
                title: 'Senior Product Manager',
                company: 'Acme Corp',
                description: '',
                location: 'Remote',
                remote: 'yes',
                salary: '',
                generation_status: 'ready',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                notes: [],
                statuses: [],
                persons: [],
                job_interviews: [],
                interviews: [],
            });
            setLoading(false);
            return;
        }
    }, [demoMode, jobId]);

    useEffect(() => {
        // If in demo mode, never make API calls
        if (demoMode || demoModeRef.current) {
            return;
        }
        
        const fetchJob = async () => {
            // Final check before making API call
            if (demoModeRef.current || demoMode) {
                setLoading(false);
                return;
            }
            
            try {
                const jobs = await api.getJobs();
                
                // Check again before processing response
                if (demoModeRef.current || demoMode) {
                    setLoading(false);
                    return;
                }
                
                const foundJob = jobs.find(j => j.id === jobId);
                setJob(foundJob || null);
            } catch (error) {
                console.error('Failed to fetch job:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchJob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, demoMode]);

    if (loading) {
        return <span className="text-xs text-gray-500">Loading job...</span>;
    }

    if (!job) {
        return <span className="text-xs text-gray-500">Job not found</span>;
    }

    const jobName = job.title ? `${job.title} - ${job.company}` : job.company;

    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                if (!demoMode) {
                    navigate(`/jobs/${jobId}`);
                }
            }}
            className={`inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors ${demoMode ? 'cursor-default' : ''}`}
            disabled={demoMode}
        >
            <span>Target: {jobName}</span>
            {!demoMode && <ExternalLink className="w-3 h-3" />}
        </button>
    );
};

interface PitchAnalysisProps {
    demoMode?: boolean;
    embed?: boolean;
    demoPitch?: PitchAnalysisResponse;
}

const PitchAnalysis: React.FC<PitchAnalysisProps> = ({ demoMode = false, embed = false, demoPitch }) => {
    const navigate = useNavigate();
    const { pitchId } = useParams<{ pitchId: string }>();
    const { api } = useApiClient();
    const { user } = useAuth();

    const [pitch, setPitch] = useState<PitchAnalysisResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [isPolling, setIsPolling] = useState(false);
    const [mobilePopup, setMobilePopup] = useState<string | null>(null);

    useEffect(() => {
        if (demoMode) {
            if (demoPitch) setPitch(demoPitch);
            setLoading(false);
            return;
        }
        if (pitchId) {
            fetchPitch();
        }
    }, [pitchId, demoMode, demoPitch]);

    // Polling for grading status
    useEffect(() => {
        if (demoMode) return;
        if (pitch && pitch.status === 'grading') {
            setIsPolling(true);
            const interval = setInterval(async () => {
                try {
                    const updatedPitch = await api.getPitchAnalysis(pitchId!);
                    setPitch(updatedPitch);
                    if (updatedPitch.status !== 'grading') {
                        setIsPolling(false);
                        clearInterval(interval);
                    }
                } catch (error) {
                    console.error('Failed to poll pitch status:', error);
                    setIsPolling(false);
                    clearInterval(interval);
                }
            }, 5000); // Poll every 5 seconds

            return () => {
                clearInterval(interval);
                setIsPolling(false);
            };
        } else {
            setIsPolling(false);
        }
    }, [pitch?.status, pitchId, api, demoMode]);

    const fetchPitch = async () => {
        if (!pitchId) return;

        try {
            setLoading(true);
            const pitchData = await api.getPitchAnalysis(pitchId);
            setPitch(pitchData);
        } catch (error) {
            console.error('Failed to fetch pitch analysis:', error);
            toast.error('Failed to load pitch analysis');
            if (!demoMode) navigate('/pitch');
        } finally {
            setLoading(false);
        }
    };

    const getStatusDisplay = (status: string) => {
        switch (status.toLowerCase()) {
            case 'ready':
            case 'complete':
                return {
                    badge: <Badge className="bg-green-100 text-green-800 border-green-200">Analysis Complete</Badge>,
                    icon: CheckCircle,
                    iconColor: 'text-green-600'
                };
            case 'grading':
                return {
                    badge: <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Grading</Badge>,
                    icon: Clock,
                    iconColor: 'text-yellow-600'
                };
            case 'analyzing':
            case 'pending':
                return {
                    badge: <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Analyzing</Badge>,
                    icon: Clock,
                    iconColor: 'text-yellow-600'
                };
            case 'error':
                return {
                    badge: <Badge className="bg-red-100 text-red-800 border-red-200">Analysis Failed</Badge>,
                    icon: Target,
                    iconColor: 'text-red-600'
                };
            default:
                return {
                    badge: <Badge className="bg-gray-100 text-gray-800 border-gray-200">{status}</Badge>,
                    icon: Clock,
                    iconColor: 'text-gray-600'
                };
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFocusDisplay = (pitch: PitchAnalysisResponse) => {
        const pitchType = pitch.pitch_type || 'general';
        switch (pitchType) {
            case 'general':
                return 'You';
            case 'employment':
                return 'Employment';
            case 'education':
                return 'Education';
            default:
                return 'You';
        }
    };

    // Score letter calculation helper
    function getScoreLetter(score: number | null | undefined) {
        if (score == null) return '-';
        else if (score > 3.9) return "S"
        else if (score > 3.4) return "A"
        else if (score > 2.9) return "B"
        else if (score > 1.9) return "C"
        else if (score > 1.4) return "D"
        else return "F"
    }

    // Grade background for overall badge (InterviewFeedback pattern)
    function getOverallGradeBg(score: number | null | undefined) {
        const letter = getScoreLetter(score);
        switch (letter) {
            case 'S': return 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-amber-900 border border-amber-300 shadow-lg shadow-amber-300/30';
            case 'A': return 'bg-green-300 text-green-900';
            case 'B': return 'bg-green-200 text-green-700';
            case 'C': return 'bg-yellow-200 text-yellow-700';
            case 'D': return 'bg-red-200 text-red-700';
            case 'F': return 'bg-red-500 text-red-900';
            default: return 'bg-gray-100 text-gray-800';
        }
    }

    // Header background color based on grade
    function getHeaderGradeBg(score: number | null | undefined) {
        const letter = getScoreLetter(score);
        switch (letter) {
            case 'S': return 'bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200';
            case 'A': return 'bg-gradient-to-br from-green-100 to-green-200';
            case 'B': return 'bg-gradient-to-br from-green-50 to-green-100';
            case 'C': return 'bg-gradient-to-br from-yellow-100 to-yellow-200';
            case 'D': return 'bg-gradient-to-br from-red-100 to-red-200';
            case 'F': return 'bg-gradient-to-br from-red-200 to-red-300';
            default: return 'bg-gradient-to-br from-blue-50 to-cyan-50';
        }
    }

    // Grade text color for header
    function getGradeTextColor(score: number | null | undefined) {
        const letter = getScoreLetter(score);
        switch (letter) {
            case 'S': return 'text-amber-800';
            case 'A': return 'text-green-800';
            case 'B': return 'text-green-700';
            case 'C': return 'text-yellow-800';
            case 'D': return 'text-red-700';
            case 'F': return 'text-red-800';
            default: return 'text-gray-800';
        }
    }
    // Score badge color helper (for non-overall)
    function getScoreBadgeClass(letter: string) {
        switch (letter) {
            case 'S': return 'bg-gradient-to-r from-teal-400 to-blue-500 text-white';
            case 'A': return 'bg-blue-100 text-blue-800';
            case 'B': return 'bg-green-100 text-green-800';
            case 'C': return 'bg-yellow-100 text-yellow-800';
            case 'D': return 'bg-orange-100 text-orange-800';
            case 'F': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    }

    // Removed renderScoreRow function - no longer needed with card layout

    // Grading wait component
    const renderGradingWait = () => (
        <div className={`flex flex-col items-center justify-center ${embed ? 'min-h-0 py-4' : 'min-h-[60vh]'} px-4 animate-fade-in`}>
            <div className="bg-white dark:bg-card rounded-2xl shadow-xl p-6 sm:p-12 max-w-2xl w-full flex flex-col items-center">
                <h1 className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                    We're analyzing your pitch
                </h1>
                <img src={logo} alt="HiredCoach Logo" className="w-20 sm:w-28 mb-4" />
                <div className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                    This might take a minute...
                </div>
                <div className="flex flex-col items-center">
                    <svg className="animate-spin w-8 sm:w-12 h-8 sm:h-12 text-primary mb-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span className="text-xs sm:text-sm text-muted-foreground">Grading in progress...</span>
                </div>
                <div className="text-center text-muted-foreground mb-2 text-sm sm:text-base space-y-2 mt-6">
                    We're analyzing your pitch against your resume and best practices; we're giving you:
                </div>
                <ul className="text-left text-muted-foreground mb-4 text-sm sm:text-lg list-disc list-inside space-y-2 mt-6">
                    <li>Detailed feedback on clarity, structure, and relevance</li>
                    <li>Specific coaching tips to improve your delivery</li>
                    <li>A breakdown of your strengths and areas for improvement</li>
                </ul>
            </div>
        </div>
    );

    if (loading) {
        const content = (
            <div className="container mx-auto px-4 py-8">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                    <div className="h-64 bg-gray-200 rounded-lg mb-6"></div>
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
        return embed ? content : (
            <DashboardLayout>{content}</DashboardLayout>
        );
    }

    if (!pitch) {
        const body = (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center py-12">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Pitch Not Found</h1>
                    <p className="text-gray-600 mb-6">The pitch analysis you're looking for doesn't exist.</p>
                    <Button onClick={() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (jobId) {
        if (!demoMode) navigate(`/jobs/${jobId}`);
    } else {
        if (!demoMode) navigate('/pitch');
    }
}}>Back</Button>
                </div>
            </div>
        );
        return embed ? body : (
            <DashboardLayout>{body}</DashboardLayout>
        );
    }

    // Show grading wait screen if pitch is being graded
    if (pitch.status === 'grading' && !demoMode) {
        return embed ? renderGradingWait() : (
            <DashboardLayout>
                {renderGradingWait()}
            </DashboardLayout>
        );
    }

    const statusDisplay = getStatusDisplay(pitch.status);
    const StatusIcon = statusDisplay.icon;

    // Show loading state if still analyzing
    if (pitch.status === 'analyzing' || pitch.status === 'pending') {
        const analyzing = (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-4xl">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Button
                            variant="ghost"
                            onClick={() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (jobId) {
        if (!demoMode) navigate(`/jobs/${jobId}`);
    } else {
        if (!demoMode) navigate('/pitch');
    }
}}
                            className="flex items-center gap-2"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </Button>
                    </div>

                    {/* Analyzing State */}
                    <Card className="text-center py-12">
                        <CardContent>
                            <div className="flex justify-center mb-6">
                                <img src={logo} alt="HiredCoach" className="h-16 w-auto" />
                            </div>
                            <div className="animate-spin w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-6"></div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Your Pitch</h2>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                Our AI is carefully analyzing your elevator pitch and preparing personalized feedback.
                                This usually takes about a minute.
                            </p>
                            <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
                                <p className="text-sm text-blue-900">
                                    <strong>Your Pitch ({pitch.time_limit} seconds):</strong>
                                </p>
                                <p className="text-sm text-blue-800 mt-2 italic">
                                    "{pitch.content}"
                                </p>
                            </div>
                            <Button
                                onClick={() => { if (!demoMode) fetchPitch(); }}
                                variant="outline"
                                className="mt-6"
                            >
                                Refresh Status
                            </Button>
                        </CardContent>
                    </Card>
                </div>
        );
        return embed ? analyzing : (
            <DashboardLayout>{analyzing}</DashboardLayout>
        );
    }

    // Show error state
    if (pitch.status === 'error') {
        const errorBody = (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-4xl">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Button
                            variant="ghost"
                            onClick={() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (jobId) {
        if (!demoMode) navigate(`/jobs/${jobId}`);
    } else {
        if (!demoMode) navigate('/pitch');
    }
}}
                            className="flex items-center gap-2"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </Button>
                    </div>

                    {/* Error State */}
                    <Card className="text-center py-12">
                        <CardContent>
                            <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                                <Target className="w-10 h-10 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analysis Failed</h2>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                We encountered an error while analyzing your pitch. Please try creating a new pitch.
                            </p>
                            <div className="flex gap-4 justify-center">
                                <Button onClick={() => { if (!demoMode) navigate('/pitch/new'); }} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Try Again
                                </Button>
                                <Button variant="outline" onClick={() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (jobId) {
        if (!demoMode) navigate(`/jobs/${jobId}`);
    } else {
        if (!demoMode) navigate('/pitch');
    }
}}>
                                    Back
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
        );
        return embed ? errorBody : (
            <DashboardLayout>{errorBody}</DashboardLayout>
        );
    }

    const body = (
            <div className={`container mx-auto ${embed ? 'px-0 py-4' : 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8'} max-w-6xl`}>
                {/* Back Button */}
                <div className="mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (jobId) {
        if (!demoMode) navigate(`/jobs/${jobId}`);
    } else {
        if (!demoMode) navigate('/pitch');
    }
}}
                        className="flex items-center gap-2"
                        disabled={demoMode}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                </div>

                {/* Header with Colored Background Based on Grade */}
                <Card className="mb-8 overflow-hidden">
                    <CardHeader className={`${getHeaderGradeBg(pitch.overall_score)} border-b border-gray-200`}>
                        <div className="flex flex-col sm:flex-row justify-between items-center sm:items-center gap-4">
                            <div className="flex items-center gap-3 text-center sm:text-left">
                                <StatusIcon className={`h-6 w-6 sm:h-8 sm:w-8 text-gray-700`} />
                                <div>
                                    <CardTitle className="text-lg sm:text-2xl text-gray-800">
                                        "You" Pitch Analysis
                                    </CardTitle>
                                    <CardDescription className="text-xs sm:text-base text-gray-600">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                            <span>{formatDate(pitch.created_at)}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span>{pitch.time_limit} seconds</span>
                                            <span className="hidden sm:inline">•</span>
                                            <Badge variant="outline" className="w-fit text-xs">
                                                Focus: {getFocusDisplay(pitch)}
                                            </Badge>
                                            {pitch.job_id && (
                                                <>
                                                    <span className="hidden sm:inline">•</span>
                                                    <JobNameDisplay jobId={pitch.job_id} demoMode={demoMode} />
                                                </>
                                            )}
                                        </div>
                                    </CardDescription>
                                </div>
                            </div>
                            {/* Large Grade Letter */}
                            {pitch.status === 'ready' || pitch.status === 'complete' ? (
                                <div className="flex flex-col items-center justify-center">
                                    <span className={`text-4xl sm:text-6xl font-bold drop-shadow-sm ${getGradeTextColor(pitch.overall_score)}`}>
                                        {getScoreLetter(pitch.overall_score)}
                                    </span>
                                    <span className="text-xs mt-1 text-gray-600 font-medium uppercase tracking-wider">Grade</span>
                                </div>
                            ) : (
                                statusDisplay.badge
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">Your Pitch:</h4>
                            <div className="flex items-start gap-3">
                                <UserAvatar user={user} className="h-11 w-11 border-0 mt-0.5" />
                                <p className="text-gray-700 leading-relaxed italic">
                                    "{pitch.content}"
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Overall Assessment Card */}
                <Card className="mb-8">
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b">
                        <CardTitle className="flex items-center gap-2 text-blue-900">
                            <MessageCircle className="w-5 h-5" />
                            Overall Assessment
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                            {pitch.summary}
                        </p>
                    </CardContent>
                </Card>

                {/* Strengths and Areas for Improvement Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Strengths */}
                    <Card>
                        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
                            <CardTitle className="flex items-center gap-2 text-green-900">
                                <TrendingUp className="w-5 h-5" />
                                What You Did Well
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            {pitch.strengths && pitch.strengths.length > 0 ? (
                                <ul className="space-y-3">
                                    {pitch.strengths.map((strength, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-700 text-sm sm:text-base leading-relaxed">{strength}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-600 text-sm sm:text-base">No specific strengths identified.</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Areas for Improvement */}
                    <Card>
                        <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b">
                            <CardTitle className="flex items-center gap-2 text-yellow-900">
                                <Target className="w-5 h-5" />
                                Areas for Improvement
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            {pitch.areas_for_improvement && pitch.areas_for_improvement.length > 0 ? (
                                <ul className="space-y-3">
                                    {pitch.areas_for_improvement.map((area, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <Target className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-700 text-sm sm:text-base leading-relaxed">{area}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-600 text-sm sm:text-base">No specific areas for improvement identified.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Coaching Tips Card */}
                <Card className="mb-8">
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b">
                        <CardTitle className="flex items-center gap-2 text-purple-900">
                            <Lightbulb className="w-5 h-5" />
                            Coaching Tips
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {pitch.coaching_tips && pitch.coaching_tips.length > 0 ? (
                            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                                <div className="flex-shrink-0 w-16 sm:w-20 mx-auto sm:mx-0">
                                    <img
                                        src={logo}
                                        alt="AI Coach"
                                        className="w-16 sm:w-20 object-contain"
                                    />
                                </div>
                                <div className="flex-1 w-full">
                                    <ul className="space-y-4">
                                        {pitch.coaching_tips.map((tip, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <Lightbulb className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                                                <span className="text-gray-700 text-base sm:text-lg leading-relaxed">{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-600 text-sm sm:text-base">No specific coaching tips available.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Score Breakdown Card */}
                <Card className="mb-8">
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                        <CardTitle className="flex items-center gap-2 text-blue-900">
                            <CheckCircle className="w-5 h-5" />
                            Score Breakdown
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start">
                            {/* Overall Grade - left side */}
                            <div className={`flex flex-col items-center justify-center rounded-xl py-8 px-8 ${getOverallGradeBg(pitch.overall_score)} shadow-lg flex-shrink-0`}>
                                <span className="text-6xl sm:text-7xl font-extrabold drop-shadow-sm">
                                    {getScoreLetter(pitch.overall_score)}
                                </span>
                                <span className="text-sm mt-2 font-medium uppercase tracking-wider">Overall Grade</span>
                            </div>

                            {/* Individual Scores - right side */}
                            <div className="flex-1 space-y-3 w-full">
                                {/* Clarity */}
                                <div className="group relative rounded-lg p-4 bg-blue-100 hover:bg-blue-200 transition-colors">
                                    {/* Desktop: flex layout with inline explanation */}
                                    <div className="hidden sm:flex items-center gap-4">
                                        <span className="inline-block font-bold text-5xl text-blue-800 w-16 text-center flex-shrink-0">
                                            {getScoreLetter(pitch.clarity_score)}
                                        </span>
                                        <div className="flex-1">
                                            <div className="text-blue-900 font-semibold text-xl mb-1">Clarity</div>
                                            <div className="text-blue-700 text-sm leading-relaxed">
                                                How clear and easy to understand your pitch was. Measures coherence, word choice, and overall comprehensibility.
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Mobile: clickable layout with popup */}
                                    <div className="sm:hidden">
                                        <div 
                                            className="flex items-center gap-4 cursor-pointer"
                                            onClick={() => setMobilePopup(mobilePopup === 'clarity' ? null : 'clarity')}
                                        >
                                            <span className="inline-block font-bold text-3xl text-blue-800 w-12 text-center">
                                                {getScoreLetter(pitch.clarity_score)}
                                            </span>
                                            <span className="text-blue-900 font-semibold flex-1 text-xl">Clarity</span>
                                        </div>
                                        {mobilePopup === 'clarity' && (
                                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                <div className="text-blue-700 text-sm leading-relaxed">
                                                    How clear and easy to understand your pitch was. Measures coherence, word choice, and overall comprehensibility.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Structure */}
                                <div className="group relative rounded-lg p-4 bg-green-100 hover:bg-green-200 transition-colors">
                                    {/* Desktop: flex layout with inline explanation */}
                                    <div className="hidden sm:flex items-center gap-4">
                                        <span className="inline-block font-bold text-5xl text-green-800 w-16 text-center flex-shrink-0">
                                            {getScoreLetter(pitch.structure_score)}
                                        </span>
                                        <div className="flex-1">
                                            <div className="text-green-900 font-semibold text-xl mb-1">Structure</div>
                                            <div className="text-green-700 text-sm leading-relaxed">
                                                How well organized your pitch was using the Past → Present → Future framework. Measures logical flow and storytelling.
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Mobile: clickable layout with popup */}
                                    <div className="sm:hidden">
                                        <div 
                                            className="flex items-center gap-4 cursor-pointer"
                                            onClick={() => setMobilePopup(mobilePopup === 'structure' ? null : 'structure')}
                                        >
                                            <span className="inline-block font-bold text-3xl text-green-800 w-12 text-center">
                                                {getScoreLetter(pitch.structure_score)}
                                            </span>
                                            <span className="text-green-900 font-semibold flex-1 text-xl">Structure</span>
                                        </div>
                                        {mobilePopup === 'structure' && (
                                            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                                <div className="text-green-700 text-sm leading-relaxed">
                                                    How well organized your pitch was using the Past → Present → Future framework. Measures logical flow and storytelling.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Relevance */}
                                <div className="group relative rounded-lg p-4 bg-yellow-100 hover:bg-yellow-200 transition-colors">
                                    {/* Desktop: flex layout with inline explanation */}
                                    <div className="hidden sm:flex items-center gap-4">
                                        <span className="inline-block font-bold text-5xl text-yellow-800 w-16 text-center flex-shrink-0">
                                            {getScoreLetter(pitch.relevance_score)}
                                        </span>
                                        <div className="flex-1">
                                            <div className="text-yellow-900 font-semibold text-xl mb-1">Relevance</div>
                                            <div className="text-yellow-700 text-sm leading-relaxed">
                                                How well your pitch aligned with your resume and career goals. Measures authenticity and resume consistency.
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Mobile: clickable layout with popup */}
                                    <div className="sm:hidden">
                                        <div 
                                            className="flex items-center gap-4 cursor-pointer"
                                            onClick={() => setMobilePopup(mobilePopup === 'relevance' ? null : 'relevance')}
                                        >
                                            <span className="inline-block font-bold text-3xl text-yellow-800 w-12 text-center">
                                                {getScoreLetter(pitch.relevance_score)}
                                            </span>
                                            <span className="text-yellow-900 font-semibold flex-1 text-xl">Relevance</span>
                                        </div>
                                        {mobilePopup === 'relevance' && (
                                            <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <div className="text-yellow-700 text-sm leading-relaxed">
                                                    How well your pitch aligned with your resume and career goals. Measures authenticity and resume consistency.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
    );

    return embed ? body : (
        <DashboardLayout>{body}</DashboardLayout>
    );
};

export default PitchAnalysis;
